<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import { registerLocalDirectoryEntry } from '../utils/localVirtualFiles'

interface SceneSpatialEventItem {
  id: string
  message: string
}

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    badge?: string
    proxyMeshUrl: string
    debugVisible: boolean
    bvhVisible: boolean
    bvhDepth: number
    gsVisible?: boolean
    worldState: string
    worldId?: string
    bindingId?: string
    bindingDuration?: string
    loadDisabled?: boolean
    clearDisabled?: boolean
    allowDirectory?: boolean
    directoryEntryNames?: string[]
    directoryExtensions?: string[]
    directoryPickLabel?: string
    events?: SceneSpatialEventItem[]
  }>(),
  {
    title: '空间查询',
    subtitle:
      '绑定代理网格，查看 world 与 binding 状态，并切换空间查询调试显示。',
    badge: '空间查询',
    gsVisible: true,
    worldId: '',
    bindingId: '',
    bindingDuration: '',
    loadDisabled: false,
    clearDisabled: false,
    allowDirectory: true,
    directoryEntryNames: () => [],
    directoryExtensions: () => ['glb', 'gltf', 'ply'],
    directoryPickLabel: '选择代理目录',
    events: () => [],
  },
)

const emit = defineEmits<{
  (event: 'update:proxyMeshUrl', value: string): void
  (event: 'update:debugVisible', value: boolean): void
  (event: 'update:bvhVisible', value: boolean): void
  (event: 'update:bvhDepth', value: number): void
  (event: 'update:gsVisible', value: boolean): void
  (event: 'load'): void
  (event: 'clear'): void
}>()

const directoryInputRef = useTemplateRef('directoryInputRef')
const pickerNotice = ref('')
let releaseLocalSession: (() => void) | null = null

const selectionSummary = computed(() => {
  if (pickerNotice.value) {
    return pickerNotice.value
  }

  return '可直接输入 URL，也可以选择本地目录，卡片会自动识别 .glb / .gltf / .ply 入口文件。'
})

function openDirectoryPicker() {
  directoryInputRef.value?.click()
}

function replaceLocalSession(release?: () => void): void {
  releaseLocalSession?.()
  releaseLocalSession = release ?? null
}

function updateProxyMeshUrl(value: string): void {
  replaceLocalSession()
  emit('update:proxyMeshUrl', value)
}

function clearBinding(): void {
  replaceLocalSession()
  emit('clear')
}

function handleDirectorySelection(event: Event) {
  const input = event.target as HTMLInputElement | null
  const files = input?.files

  if (!input || !files || files.length === 0) {
    return
  }

  const { entryUrl, entryPath, totalFiles, release } = registerLocalDirectoryEntry(files, {
    entryFileNames: props.directoryEntryNames,
    entryExtensions: props.directoryExtensions,
  })

  if (entryUrl && entryPath) {
    replaceLocalSession(release)
    pickerNotice.value = `已选择本地目录，定位到 ${entryPath}，共扫描 ${totalFiles} 个文件。`
    emit('update:proxyMeshUrl', entryUrl)
  } else {
    const expectedNames = props.directoryEntryNames.join(' / ')
    const expectedExtensions = props.directoryExtensions.map((item) => `.${item}`).join(' / ')
    const expected = [expectedNames, expectedExtensions].filter(Boolean).join(' 或 ')
    pickerNotice.value = `已选择本地目录，但未找到匹配的入口文件。期望文件：${expected || '支持的代理模型文件'}。`
  }

  input.value = ''
}

onBeforeUnmount(() => replaceLocalSession())
</script>

<template>
  <details class="panel-subsection" open>
    <summary class="panel-subsection-summary">
      <span class="summary-main">
        <span class="panel-subsection-title">{{ title }}</span>
        <span class="panel-section-subtitle">{{ subtitle }}</span>
      </span>
      <span class="summary-side">
        <span class="card-badge">{{ badge }}</span>
        <span class="summary-chevron" aria-hidden="true">v</span>
      </span>
    </summary>

    <div class="panel-subsection-body stacked-panel-body">
      <div class="panel-selection-summary">
        <span class="panel-selection-label">当前选择</span>
        <p class="panel-selection-text">{{ selectionSummary }}</p>
      </div>

      <label class="panel-field">
        <span class="panel-field-label">代理网格 URL</span>
        <input
          class="panel-input"
          :value="proxyMeshUrl"
          placeholder="proxy.glb / proxy.ply"
          @input="updateProxyMeshUrl(($event.target as HTMLInputElement).value)"
        />
      </label>

      <div class="button-grid">
        <button
          v-if="props.allowDirectory"
          class="panel-button panel-button-subtle"
          type="button"
          @click.stop="openDirectoryPicker"
        >
          <span class="panel-button-label">{{ props.directoryPickLabel }}</span>
          <span class="panel-button-meta">从本地目录自动识别代理网格入口文件</span>
        </button>
        <button
          class="panel-button"
          :disabled="loadDisabled"
          @click="emit('load')"
        >
          <span class="panel-button-label">绑定代理网格</span>
          <span class="panel-button-meta">创建代理查询世界并绑定到当前 3DGS</span>
        </button>
        <button
          class="panel-button panel-button-subtle"
          :disabled="clearDisabled"
          @click="clearBinding"
        >
          <span class="panel-button-label">清除绑定</span>
          <span class="panel-button-meta">释放当前 world 与 binding</span>
        </button>
      </div>

      <div class="toggle-group">
        <label class="debug-toggle">
          <input
            :checked="gsVisible"
            type="checkbox"
            @change="emit('update:gsVisible', ($event.target as HTMLInputElement).checked)"
          />
          <span>显示 3DGS 场景</span>
        </label>

        <label class="debug-toggle">
          <input
            :checked="debugVisible"
            type="checkbox"
            @change="emit('update:debugVisible', ($event.target as HTMLInputElement).checked)"
          />
          <span>显示代理网格线框</span>
        </label>

        <label class="debug-toggle">
          <input
            :checked="bvhVisible"
            type="checkbox"
            @change="emit('update:bvhVisible', ($event.target as HTMLInputElement).checked)"
          />
          <span>显示 BVH 包围盒</span>
        </label>
      </div>

      <label class="range-field">
        <span class="range-header">
          <span class="panel-field-label">BVH 深度</span>
          <strong class="range-value">{{ bvhDepth }}</strong>
        </span>
        <input
          class="range-input"
          type="range"
          min="1"
          max="20"
          step="1"
          :value="bvhDepth"
          @input="emit('update:bvhDepth', Number(($event.target as HTMLInputElement).value))"
        />
        <span class="range-note">`1` 只显示根包围盒，数值越大，展示的 BVH 节点层级越深。</span>
      </label>

      <div class="status-grid spatial-status-grid">
        <div class="status-item">
          <span class="status-label">世界状态</span>
          <strong class="status-value">{{ worldState }}</strong>
        </div>
        <div class="status-item">
          <span class="status-label">绑定状态</span>
          <strong class="status-value">{{ bindingId || '未绑定' }}</strong>
        </div>
      </div>

      <div class="spatial-meta">
        <span class="status-label">世界 ID</span>
        <code class="spatial-meta-value">{{ worldId || '未创建' }}</code>
      </div>

      <div class="spatial-meta">
        <span class="status-label">最近绑定用时</span>
        <code class="spatial-meta-value">{{ bindingDuration || '暂无记录' }}</code>
      </div>

      <div class="spatial-events">
        <span class="panel-field-label">最近事件</span>
        <ul v-if="events.length > 0" class="spatial-event-list">
          <li v-for="item in events" :key="item.id">{{ item.message }}</li>
        </ul>
        <p v-else class="panel-note">还没有空间查询事件，先加载 3DGS，再绑定代理网格。</p>
      </div>
    </div>
  </details>

  <input
    v-if="props.allowDirectory"
    ref="directoryInputRef"
    class="loader-file-input"
    type="file"
    webkitdirectory
    directory
    multiple
    @change="handleDirectorySelection"
  />
</template>

<style scoped>
.loader-file-input {
  display: none;
}

.panel-selection-summary {
  display: grid;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(150, 170, 255, 0.16);
  background:
    linear-gradient(180deg, rgba(11, 16, 28, 0.96), rgba(18, 25, 40, 0.88));
}

.panel-selection-label {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(184, 201, 255, 0.74);
}

.panel-selection-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
  color: rgba(238, 244, 255, 0.86);
  word-break: break-word;
}

.debug-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #dbe7ef;
}

.toggle-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.range-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.range-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.range-value {
  font-size: 13px;
  color: #f8fafc;
}

.range-input {
  width: 100%;
}

.range-note {
  font-size: 12px;
  color: rgba(219, 231, 239, 0.72);
  line-height: 1.5;
}

.spatial-status-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.spatial-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spatial-meta-value {
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(8, 13, 20, 0.56);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #dbe7ef;
  font-size: 12px;
  word-break: break-all;
}

.spatial-events {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.spatial-event-list {
  margin: 0;
  padding-left: 18px;
  color: #dbe7ef;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
